import React, { useState } from "react";

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    OutlinedInput,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { formatBytes, formatDate } from "../../../../utilities/formatters";
import SearchIcon from "@mui/icons-material/Search";

const runtimeConfig = window.__APP_CONFIG__ || {};

const authSystemClientId =
    runtimeConfig.AUTH_SYSTEM_CLIENT_ID ||
    process.env.REACT_APP_AUTH_SYSTEM_CLIENT_ID;


const suppliers = [
    { code: "PIT", name: "Premier IT" },
    { code: "ALLOCATE", name: "Allocate" },
    { code: "CLARITY", name: "Clarity" },
    { code: "L2P", name: "L2P" },
    { code: "SARD", name: "Sard" },
    { code: "14FISH", name: "FourteenFish" },
    { code: "N/A", name: "Unknown" },
];

const documentTypes = [
    { code: "N/A", name: "No supporting documentation", fileType: "" },
    { code: "SUMMARY", name: "Appraisal summary (.pdf)", fileType: ".pdf" },
    {
        code: "OUTPUT",
        name: "Appraisal output summary (.pdf)",
        fileType: ".pdf",
    },
    {
        code: "MAG-v4.2",
        name: "Medical appraisal guide MAG 4.2 (.pdf)",
        fileType: ".pdf",
    },
    {
        code: "MAG-v4.1",
        name: "Medical appraisal guide MAG 4.1 (.pdf)",
        fileType: ".pdf",
    },
    {
        code: "MAG-v4.0",
        name: "Medical appraisal guide MAG 4.0 (.pdf)",
        fileType: ".pdf",
    },
    {
        code: "MAG-v3.0",
        name: "Medical appraisal guide MAG 3.0 (.pdf)",
        fileType: ".pdf",
    },
    { code: "PORTFOLIO", name: "Portfolio (.zip)", fileType: ".zip" },
];

const Input = styled("input")({
    display: "none",
});

function AppraisalAddFormDialog({ open, onSave, onClose, ...props }) {
    const today = new Date(); // get today's date
    const twoYearsLater = new Date(
        today.getFullYear() + 2,
        today.getMonth(),
        today.getDate()
    );

    const minStartDate = "2010-01-01 00:00:00";
    const maxStartDate = twoYearsLater;
    const maxMeetingDate = maxStartDate;

    const [title, setTitle] = useState("");
    const [appraiserName, setAppraiserName] = useState("");
    const [appraiserEmail, setAppraiserEmail] = useState("");
    const [startDate, setStartDate] = useState("");
    const [meetingDate, setMeetingDate] = useState("");
    const [notes, setNotes] = useState("");

    const [supplier, setSupplier] = useState("");
    const [documentType, setDocumentType] = useState("");
    const [file, setFile] = useState("");
    const [fileType, setFileType] = useState(".pdf");

    const handleSelectDocumentType = (event) => {
        const code = event.target.value; // Access the value property of the event target
        const documentType = documentTypes.find((item) => item.code === code);
        const fileType = documentType?.fileType;

        setDocumentType(code);
        setFileType(fileType);
    };

    const handleSelectFile = (event) => {
        setFile(event.target.files[0] || null);
    };

    const handleClose = () => {
        setTitle("");
        setAppraiserName("");
        setAppraiserEmail("");
        setStartDate("");
        setMeetingDate("");
        setNotes("");
        setSupplier("");
        onClose();
    };

    const handleSubmit = () => {
        const params = {
            title,
            appraiser: [{ name: appraiserName, email: appraiserEmail }],
            cycle: { start: formatDate(startDate) },
            meeting: {
                date: formatDate(meetingDate),
                notes,
            },
            origin: {
                name: authSystemClientId,
                type: "external",
                template: "N/A",
                supplier,
            },
            import: {
                date: formatDate(dayjs(today)),
                name: "John Doe",
                method: "form",
                documentType,
                file,
            },
        };
        onSave(params);
        handleClose();
    };

    return (
        <Dialog
            open={!!open}
            onClose={handleClose}
            fullWidth={true}
            maxWidth="md"
            {...props}
        >
            <DialogTitle>Add historic appraisal</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!title}
                    >
                        <InputLabel
                            id="label-title"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Title
                        </InputLabel>
                        <OutlinedInput
                            id="text-title"
                            value={title}
                            onChange={({ target }) => setTitle(target.value)}
                            inputProps={{ maxLength: 80 }}
                            multiline={false}
                            autoFocus={true}
                        />
                        <FormHelperText>
                            Give this appraisal a tile (up to 80 characters)
                        </FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={true}
                        error={!appraiserName}
                    >
                        <InputLabel
                            id="label-appraiser"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Appraiser Name
                        </InputLabel>
                        <OutlinedInput
                            id="text-appraiser"
                            value={appraiserName}
                            onChange={({ target }) =>
                                setAppraiserName(target.value)
                            }
                            inputProps={{ maxLength: 120 }}
                            multiline={false}
                        />
                        <FormHelperText>
                            Full name of the appraiser (up to 120 characters)
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth
                        required={false}
                    >
                        <InputLabel
                            id="label-appraiser"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Appraiser Email
                        </InputLabel>
                        <OutlinedInput
                            id="text-appraiser-email"
                            value={appraiserEmail}
                            onChange={({ target }) =>
                                setAppraiserEmail(target.value)
                            }
                            inputProps={{ maxLength: 120 }}
                            multiline={false}
                        />
                        <FormHelperText>
                            Email address of the appraiser (up to 120
                            characters)
                        </FormHelperText>
                    </FormControl>

                    <Box className="flex-container">
                        <FormControl
                            component="div"
                            margin={"normal"}
                            error={!startDate}
                        >
                            <DatePicker
                                id="date-start"
                                label="Start"
                                format="DD/MM/YYYY"
                                value={dayjs(startDate)}
                                minDate={dayjs(minStartDate)}
                                maxDate={dayjs(maxStartDate)}
                                onChange={setStartDate}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                            />
                        </FormControl>

                        <FormControl component="div" margin={"normal"}>
                            <DatePicker
                                id="date-meeting"
                                label="Meeting"
                                format="DD/MM/YYYY"
                                value={dayjs(meetingDate)}
                                minDate={dayjs(startDate || minStartDate)}
                                maxDate={dayjs(maxMeetingDate)}
                                onChange={setMeetingDate}
                                renderInput={(params) => (
                                    <TextField {...params} />
                                )}
                                required
                            />
                        </FormControl>
                    </Box>

                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth={true}
                        required={true}
                        error={!supplier}
                    >
                        <InputLabel
                            id="label-type"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Supplier
                        </InputLabel>
                        <Select
                            id="select-supplier"
                            value={supplier}
                            onChange={({ target }) => setSupplier(target.value)}
                        >
                            {suppliers?.length > 0 &&
                                suppliers.map((supplier) => (
                                    <MenuItem
                                        value={supplier.code}
                                        key={supplier.code}
                                    >
                                        {supplier.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>
                            Select the appraisal system supplier (choose
                            'Unknown' if unknown)
                        </FormHelperText>
                    </FormControl>

                    <FormControl
                        component="div"
                        margin="normal"
                        fullWidth={true}
                        required={true}
                        error={!documentType}
                    >
                        <InputLabel
                            id="label-type"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Supporting documentation
                        </InputLabel>
                        <Select
                            id="select-documentType"
                            value={documentType}
                            onChange={(event) =>
                                handleSelectDocumentType(event)
                            }
                        >
                            {documentTypes?.length > 0 &&
                                documentTypes.map((option) => (
                                    <MenuItem
                                        value={option.code}
                                        key={option.code}
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>
                            Are you including any supporting documentation
                        </FormHelperText>
                    </FormControl>

                    {documentType && documentType !== "N/A" && (
                        <>
                            <FormControl
                                component="div"
                                margin={"normal"}
                                fullWidth={true}
                            >
                                <label htmlFor="contained-button-file">
                                    <Input
                                        accept={fileType}
                                        id="contained-button-file"
                                        type="file"
                                        onChange={handleSelectFile}
                                    />
                                    <Button
                                        component="span"
                                        variant="contained"
                                        color="neutral"
                                        disableElevation={true}
                                        aria-label="browse for a file"
                                        startIcon={<SearchIcon />}
                                    >
                                        Browse
                                    </Button>
                                    {!!file && (
                                        <Box
                                            component="span"
                                            sx={{ paddingLeft: "1em" }}
                                        >
                                            {file.name} -{" "}
                                            {formatBytes(file.size)}
                                        </Box>
                                    )}
                                </label>
                            </FormControl>
                        </>
                    )}

                    <FormControl
                        component="div"
                        margin={"normal"}
                        fullWidth={true}
                        required={false}
                    >
                        <InputLabel
                            id="label-notes"
                            sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                        >
                            Notes
                        </InputLabel>
                        <OutlinedInput
                            id="text-notes"
                            value={notes}
                            onChange={({ target }) => setNotes(target.value)}
                            inputProps={{ maxLength: 250 }}
                            multiline={true}
                            rows={2}
                        />
                        <FormHelperText>
                            Add any notes you feel may be useful (up to 250
                            characters)
                        </FormHelperText>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    key="add"
                    variant="contained"
                    color="primary"
                    disabled={
                        !title ||
                        !appraiserName ||
                        !meetingDate ||
                        !supplier ||
                        !documentType
                    }
                    onClick={handleSubmit}
                    disableElevation={true}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AppraisalAddFormDialog;
